import { useState } from 'react';
import { CivilLawsuitBucketHit, CauseOfActionEntry } from '../types';

interface TrendResultsListProps {
  hits: CivilLawsuitBucketHit[];
  totalCount: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  isZoomed?: boolean;
  onPageChange: (page: number) => void;
}

export const TrendResultsList = ({
  hits,
  totalCount,
  page,
  pageSize,
  isLoading,
  isZoomed,
  onPageChange
}: TrendResultsListProps) => {
  const [selectedHit, setSelectedHit] = useState<CivilLawsuitBucketHit | null>(null);
  const [pageInput, setPageInput] = useState('');
  const maxResults = 10000;
  const effectiveTotal = Math.min(totalCount, maxResults);
  const totalPages = Math.ceil(effectiveTotal / pageSize);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handlePageInputSubmit = () => {
    const num = parseInt(pageInput, 10);
    if (!isNaN(num) && num >= 1 && num <= totalPages) {
      onPageChange(num);
      setPageInput('');
    }
  };

  return (
    <div data-component="TrendResultsList" className="bg-white rounded-lg shadow-sm border border-gray-200 relative">
      {/* Header with pagination */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          {isLoading ? 'Loading...' : (
            <>
              Results — {totalCount.toLocaleString()} records
              {isZoomed && (
                <span className="text-sm font-normal text-blue-600 ml-2">(zoomed range)</span>
              )}
              {totalCount > maxResults && (
                <span className="text-sm font-normal text-amber-600 ml-2">(browsable: first {maxResults.toLocaleString()})</span>
              )}
            </>
          )}
        </h3>
        {totalPages > 1 && (
          <PaginationControls
            page={page}
            totalPages={totalPages}
            isLoading={isLoading}
            pageInput={pageInput}
            onPageChange={onPageChange}
            onPageInputChange={setPageInput}
            onPageInputSubmit={handlePageInputSubmit}
          />
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-8 text-center text-gray-500">Loading records...</div>
      ) : hits.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No records found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Case #</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Filed</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Judge</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plaintiffs</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Defendants</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nature of Suit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {hits.map((hit) => (
                <ResultRow
                  key={hit.id}
                  hit={hit}
                  isSelected={selectedHit?.id === hit.id}
                  onSelect={() => setSelectedHit(hit)}
                  formatDate={formatDate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bottom Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center p-4 border-t border-gray-200">
          <PaginationControls
            page={page}
            totalPages={totalPages}
            isLoading={isLoading}
            pageInput={pageInput}
            onPageChange={onPageChange}
            onPageInputChange={setPageInput}
            onPageInputSubmit={handlePageInputSubmit}
          />
        </div>
      )}

      {/* Backdrop overlay */}
      {selectedHit && (
        <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setSelectedHit(null)} />
      )}

      {/* Slide-out detail panel */}
      {selectedHit && (
        <DetailPanel
          hit={selectedHit}
          onClose={() => setSelectedHit(null)}
          onPrevious={() => {
            const idx = hits.findIndex(h => h.id === selectedHit.id);
            if (idx > 0) setSelectedHit(hits[idx - 1]);
          }}
          onNext={() => {
            const idx = hits.findIndex(h => h.id === selectedHit.id);
            if (idx < hits.length - 1) setSelectedHit(hits[idx + 1]);
          }}
          hasPrevious={hits.findIndex(h => h.id === selectedHit.id) > 0}
          hasNext={hits.findIndex(h => h.id === selectedHit.id) < hits.length - 1}
        />
      )}
    </div>
  );
};

const PaginationControls = ({ page, totalPages, isLoading, pageInput, onPageChange, onPageInputChange, onPageInputSubmit }: {
  page: number; totalPages: number; isLoading: boolean; pageInput: string;
  onPageChange: (p: number) => void; onPageInputChange: (v: string) => void; onPageInputSubmit: () => void;
}) => (
  <div className="flex items-center gap-2">
    <button onClick={() => onPageChange(page - 1)} disabled={page <= 1 || isLoading}
      className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
      ←
    </button>
    <span className="text-sm text-gray-600">
      {page} / {totalPages}
    </span>
    <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages || isLoading}
      className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
      →
    </button>
    <input
      type="number"
      min={1}
      max={totalPages}
      value={pageInput}
      onChange={e => onPageInputChange(e.target.value)}
      onKeyDown={e => e.key === 'Enter' && onPageInputSubmit()}
      placeholder="Go to"
      className="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
    />
    <button onClick={onPageInputSubmit}
      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
      disabled={isLoading}>
      Go
    </button>
  </div>
);

const ResultRow = ({
  hit,
  isSelected,
  onSelect,
  formatDate
}: {
  hit: CivilLawsuitBucketHit;
  isSelected: boolean;
  onSelect: () => void;
  formatDate: (d?: string) => string;
}) => (
  <tr className={`hover:bg-blue-50 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : ''}`} onClick={onSelect}>
    <td className="px-3 py-2 font-mono text-xs text-gray-400">{hit.id?.split('_').slice(1).join('_') || hit.id || '—'}</td>
    <td className="px-3 py-2 font-mono text-xs">{hit.caseNumber || '—'}</td>
    <td className="px-3 py-2 whitespace-nowrap">{formatDate(hit.filedAt)}</td>
    <td className="px-3 py-2">{hit.courtState || '—'}</td>
    <td className="px-3 py-2 max-w-[150px] truncate" title={hit.judge?.join(', ')}>
      {hit.judge?.join(', ') || '—'}
    </td>
    <td className="px-3 py-2 max-w-[200px] truncate" title={hit.plaintiffs?.join(', ')}>
      {hit.plaintiffs?.join(', ') || '—'}
    </td>
    <td className="px-3 py-2 max-w-[200px] truncate" title={hit.defendants?.join(', ')}>
      {hit.defendants?.join(', ') || '—'}
    </td>
    <td className="px-3 py-2">{hit.natureOfSuit || '—'}</td>
  </tr>
);

const ExpandedDetails = ({ hit }: { hit: CivilLawsuitBucketHit }) => (
  <div className="space-y-5 text-sm">
    {/* Core fields */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <InfoField label="Case Number" value={hit.caseNumber} />
      <InfoField label="Filed" value={hit.filedAt ? new Date(hit.filedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : undefined} />
      <InfoField label="State" value={hit.courtState} />
      <InfoField label="Nature of Suit" value={hit.natureOfSuit} />
    </div>

    {/* Court & judge grid */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <InfoField label="Court" value={hit.courtName} />
      <InfoField label="Court City" value={hit.courtWithCity || hit.courtCity} />
      <InfoField label="Judge" value={hit.judge?.join(', ')} />
      <InfoField label="Case Block ID" value={hit.caseBlockId} />
    </div>

    {/* Parties */}
    {(hit.plaintiffs?.length > 0 || hit.defendants?.length > 0) && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {hit.plaintiffs?.length > 0 && <InfoField label="Plaintiffs" value={hit.plaintiffs.join('; ')} />}
        {hit.defendants?.length > 0 && <InfoField label="Defendants" value={hit.defendants.join('; ')} />}
      </div>
    )}

    {/* Type of Action badge */}
    {hit.typeOfAction && (
      <div className="rounded-md bg-cyan-50 border border-cyan-200 px-4 py-2">
        <span className="text-xs text-gray-500 mr-2">Type of Action:</span>
        <span className="font-semibold text-gray-900">{hit.typeOfAction}</span>
      </div>
    )}

    {/* Parties & Lawyers */}
    {(hit.plaintiffLawyers?.length > 0 || hit.defendantLawyers?.length > 0) && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {hit.plaintiffLawyers?.length > 0 && <InfoField label="Plaintiff Lawyers" value={hit.plaintiffLawyers.join('; ')} />}
        {hit.defendantLawyers?.length > 0 && <InfoField label="Defendant Lawyers" value={hit.defendantLawyers.join('; ')} />}
      </div>
    )}

    {(hit.plaintiffLawyerFirms?.length > 0 || hit.defendantLawyerFirms?.length > 0) && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {hit.plaintiffLawyerFirms?.length > 0 && <InfoField label="Plaintiff Firms" value={hit.plaintiffLawyerFirms.join('; ')} />}
        {hit.defendantLawyerFirms?.length > 0 && <InfoField label="Defendant Firms" value={hit.defendantLawyerFirms.join('; ')} />}
      </div>
    )}

    {hit.summary && (
      <div>
        <span className="font-medium text-gray-700">Summary</span>
        <p className="mt-1 text-gray-600">{hit.summary}</p>
      </div>
    )}

    {/* Causes of Action — structured with name, description, allegations */}
    {hit.causesOfAction?.length > 0 && (
      <CausesOfActionSection items={hit.causesOfAction} />
    )}

    {/* Facts — individual cards with left border */}
    {hit.facts?.length > 0 && (
      <SectionWithCards label="Facts" items={hit.facts} borderColor="border-blue-500" underlineColor="border-blue-300" />
    )}

    {/* Relief Sought — individual cards with left border */}
    {hit.relief?.length > 0 && (
      <SectionWithCards label="Relief Sought" items={hit.relief} borderColor="border-green-500" underlineColor="border-green-300" />
    )}

    {hit.services?.length > 0 && <InfoField label="Services" value={hit.services.join(', ')} />}
    {hit.natureOfSuits?.length > 1 && <InfoField label="All Natures of Suit" value={hit.natureOfSuits.join(', ')} />}
  </div>
);

/** Parses legacy flattened cause string as fallback. */
const parseLegacyCause = (item: any): CauseOfActionEntry => {
  if (typeof item === 'object' && item !== null && item.name) return item as CauseOfActionEntry;
  // Try JSON string (precomputed trends stores objects as serialized JSON strings)
  if (typeof item === 'string' && item.startsWith('{')) {
    try {
      const parsed = JSON.parse(item);
      if (parsed && parsed.name) return parsed as CauseOfActionEntry;
    } catch { /* fall through to legacy parser */ }
  }
  // Legacy flattened text format
  const text = String(item);
  const lines = text.split('\n');
  const firstLine = lines[0] || '';
  const colonIdx = firstLine.indexOf(':');
  const name = colonIdx > 0 ? firstLine.slice(0, colonIdx).trim() : '';
  const description = colonIdx > 0 ? firstLine.slice(colonIdx + 1).trim() : firstLine.trim();

  const allegations: string[] = [];
  let inAllegations = false;
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line === 'Allegations:') { inAllegations = true; continue; }
    if (inAllegations && line.startsWith('- ')) {
      allegations.push(line.slice(2));
    }
  }
  return { name, categories: [], description, allegations };
};

/** Renders structured Causes of Action section with categories tags. */
const CausesOfActionSection = ({ items }: { items: CauseOfActionEntry[] }) => {
  const causes = items.map(parseLegacyCause);
  return (
    <div>
      <div className="font-semibold text-gray-900 pb-1 mb-3 border-b-2 border-purple-300">Causes of Action</div>
      <div className="space-y-4">
        {causes.map((cause, i) => (
          <div key={i} className="border-l-4 border-purple-500 pl-4 py-2">
            <div className="font-semibold text-blue-700 mb-1">
              {i + 1}. {cause.name || `Cause ${i + 1}`}
            </div>
            {cause.categories?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {cause.categories.map((cat, j) => (
                  <span key={j} className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-teal-600 text-white">
                    {cat}
                  </span>
                ))}
              </div>
            )}
            {cause.description && (
              <p className="text-gray-700 text-sm mb-2">{cause.description}</p>
            )}
            {cause.allegations?.length > 0 && (
              <div className="mt-2">
                <span className="font-medium text-gray-700 text-xs">Key Allegations:</span>
                <ul className="mt-1 space-y-1 list-disc list-inside text-gray-600 text-xs">
                  {cause.allegations.map((a, j) => <li key={j}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/** Section heading + individual bordered cards per item. */
const SectionWithCards = ({ label, items, borderColor, underlineColor }: {
  label: string; items: string[]; borderColor: string; underlineColor: string;
}) => (
  <div>
    <div className={`font-semibold text-gray-900 pb-1 mb-3 border-b-2 ${underlineColor}`}>{label}</div>
    <div className="space-y-3">
      {items.map((text, i) => (
        <div key={i} className={`border-l-4 ${borderColor} pl-4 py-2`}>
          <p className="text-gray-700 text-sm leading-relaxed">{text}</p>
        </div>
      ))}
    </div>
  </div>
);

const InfoField = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) return null;
  return (
    <div>
      <span className="font-medium text-gray-700">{label}</span>
      <p className="text-gray-600 mt-0.5">{value}</p>
    </div>
  );
};

const DetailPanel = ({ hit, onClose, onPrevious, onNext, hasPrevious, hasNext }: {
  hit: CivilLawsuitBucketHit; onClose: () => void;
  onPrevious: () => void; onNext: () => void;
  hasPrevious: boolean; hasNext: boolean;
}) => (
  <div data-component="DetailPanel" className="fixed top-0 right-0 h-full w-[50vw] max-w-[800px] min-w-[400px] bg-white shadow-2xl z-50 overflow-y-auto">
    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Case Details</h2>
        <p className="text-sm text-gray-500 font-mono">{hit.caseNumber || hit.id || '—'}</p>
        {(hit.complaintId || hit.id) && (
          <a
            href={`https://search.cnscaseportal.com/civil-litigation/case/${hit.complaintId || hit.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            Open in CasePortal ↗
          </a>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onPrevious}
          disabled={!hasPrevious}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Previous case"
        >
          ← Prev
        </button>
        <button
          onClick={onNext}
          disabled={!hasNext}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Next case"
        >
          Next →
        </button>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-2"
          aria-label="Close panel"
        >
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
    <div className="p-6">
      <ExpandedDetails hit={hit} />
    </div>
  </div>
);


